/**
 * Runtime service-line status
 *  - Fetches /api/service-lines/effective on mount (env-scoped).
 *  - Caches the result in localStorage so page reloads are instant.
 *  - Exposes a `useServiceLineStatus(lineId)` hook and `<ServiceLineGate>` wrapper.
 *
 * If the fetch fails, we fall back to the static config in `serviceLines.js`
 * so a temporary API outage doesn't kill navigation.
 */
import { useEffect, useState } from "react";
import { API } from "../App";
import { SERVICE_LINES, isPreviewModeEnabled } from "../config/serviceLines";

const CACHE_KEY = "ha_service_lines_effective_v1";
const CACHE_TTL_MS = 30 * 1000; // 30s — short enough for a snappy admin loop

// The browser is the single source of truth for which environment we're on —
// window.location.hostname can't be rewritten by proxies or shared .env files.
export function getClientEnv() {
  if (typeof window === "undefined") return "preview";
  const host = window.location.hostname;
  return host.includes(".preview.") || host === "localhost" ? "preview" : "production";
}

let inFlight = null;

async function fetchEffective() {
  if (inFlight) return inFlight;
  inFlight = fetch(`${API}/service-lines/effective?client_env=${getClientEnv()}`)
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      if (data) {
        try {
          localStorage.setItem(
            CACHE_KEY,
            JSON.stringify({ ts: Date.now(), data }),
          );
        } catch {}
      }
      return data;
    })
    .catch(() => null)
    .finally(() => { inFlight = null; });
  return inFlight;
}

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function staticFallback() {
  return {
    env: "unknown",
    lines: Object.values(SERVICE_LINES).map((l) => ({
      line_id: l.lineId,
      name: l.name,
      status: l.status,
      icon: l.icon,
      color: l.color,
      route_prefix: l.routePrefix,
    })),
  };
}

export function useEffectiveServiceLines() {
  const [state, setState] = useState(() => readCache() || staticFallback());

  useEffect(() => {
    let cancelled = false;
    fetchEffective().then((data) => {
      if (!cancelled && data) setState(data);
    });
    return () => { cancelled = true; };
  }, []);

  return state;
}

export function useServiceLineStatus(lineId) {
  const config = useEffectiveServiceLines();
  const line = config?.lines?.find((l) => l.line_id === lineId);
  return line?.status || "disabled";
}

export function isLineRuntimeEnabled(lineId, config) {
  if (!config) return false;
  const line = config.lines?.find((l) => l.line_id === lineId);
  if (!line) return false;
  if (line.status === "active") return true;
  if (line.status === "preview" && isPreviewModeEnabled()) return true;
  return false;
}
